import { useMemo } from 'react'
import type { EndpointDisplayInfo, SummaryTileData } from '../../types/infrastructure-map'

function leafChildrenFor(
  directChildren: string[],
  childrenByParent: Map<string, string[]>
): string[] {
  return directChildren.filter((childId) => {
    const childHasChildren = (childrenByParent.get(childId)?.length ?? 0) > 0
    return !childHasChildren
  })
}

function hiddenDescendantsFor(
  endpointId: string,
  displayInfo: Map<string, EndpointDisplayInfo>,
  getAllDescendants: (id: string) => string[]
): string[] {
  return getAllDescendants(endpointId).filter((descId) => {
    const descInfo = displayInfo.get(descId)
    return descInfo?.isHiddenByCollapse === true
  })
}

interface BuildTileArgs {
  endpointId: string
  childrenByParent: Map<string, string[]>
  displayInfo: Map<string, EndpointDisplayInfo>
  getAllDescendants: (id: string) => string[]
}

function buildTile(args: BuildTileArgs): SummaryTileData | null {
  const { endpointId, childrenByParent, displayInfo, getAllDescendants } = args
  const directChildren = childrenByParent.get(endpointId) ?? []
  const leafChildren = leafChildrenFor(directChildren, childrenByParent)
  const hiddenDescendants = hiddenDescendantsFor(endpointId, displayInfo, getAllDescendants)
  if (leafChildren.length === 0 && hiddenDescendants.length <= leafChildren.length) {
    return null
  }
  return {
    parentEndpointId: endpointId,
    directChildCount: leafChildren.length,
    totalDescendantCount: hiddenDescendants.length,
    isRecursive: hiddenDescendants.length > leafChildren.length,
    hiddenEndpointIds: hiddenDescendants,
  }
}

export interface UseSummaryTilesOptions {
  displayInfo: Map<string, EndpointDisplayInfo>
  childrenByParent: Map<string, string[]>
  getAllDescendants: (endpointId: string) => string[]
}

export function useSummaryTiles(options: UseSummaryTilesOptions): Map<string, SummaryTileData> {
  const { displayInfo, childrenByParent, getAllDescendants } = options
  return useMemo(() => {
    const tiles = new Map<string, SummaryTileData>()
    for (const [endpointId, info] of displayInfo.entries()) {
      if (!info.isCollapsed) continue
      const tile = buildTile({ endpointId, childrenByParent, displayInfo, getAllDescendants })
      if (tile) tiles.set(endpointId, tile)
    }
    return tiles
  }, [displayInfo, childrenByParent, getAllDescendants])
}
